import { $, $$ } from './_queryselector.js';
import * as _ from './_helpers.js';
import * as Gsap from './_gsap.js';

let paused = false;

let container;
let canvas;
let image;

export function setPaused(value) {
    paused = value;

    if (typeof canvas === 'undefined' || typeof image === 'undefined') return;

    if (paused) {
        container.classList.add('-hidden');
    } else {
        container.classList.remove('-hidden');
    }
}

export function init() {
    container = document.querySelector('.js-shapes');
    canvas = document.querySelector('.js-shapes-canvas');
    const ctx = canvas.getContext('2d');

    image = document.querySelector('.js-shapes-image');

    let canvasWidth = canvas.offsetWidth;
    let canvasHeight = canvas.offsetHeight;
    let vmax = Math.max(canvasWidth, canvasHeight);

    ctx.canvas.width = canvasWidth;
    ctx.canvas.height = canvasHeight;

    ctx.lineWidth = 100;

    const color = {
        light: '#d1bef1',
        primary: '#8b5cdd',
        dark: '#3e3750',
    };

    let radius = 0;
    let maxRadius = Math.round(0.27 * vmax);

    let width = 0;
    let maxWidth = Math.round(0.66 * vmax);
    let height = maxWidth * 0.25;

    let arcEnd = 0;

    let startTime = null;
    let progress = 0;

    // 3 different shape layouts
    let currentPos = -1; // 0-2
    const pos = {
        circle: [
            { x: 0.22, y: 0.36 },
            { x: -0.5, y: -0.6 },
            { x: 0.22, y: -0.5 },
        ],
        rect: [
            { x: -0.3, y: -0.25, rot: 15 },
            { x: 0.15, y: -0.6, rot: 110 },
            { x: 0.35, y: -0.3, rot: -78 },
        ],
        arc: [
            { x: -0.25, y: 0.28 },
            { x: -0.25, y: 0.28 },
            { x: -0.25, y: 0.28 },
        ],
    };

    // mouse position
    let mx = canvasWidth / 2;
    let my = canvasHeight / 2;
    const smooth = 0.09;
    let o = [0, 0, 0];

    let circleOffset = [0, 0];
    let rectOffset = [0, 0];
    let curveOffset = [0, 0];
    let imageOffset = [0, 0];

    window.addEventListener('resize', () => {
        canvasWidth = canvas.offsetWidth;
        canvasHeight = canvas.offsetHeight;
        vmax = Math.max(canvasWidth, canvasHeight);

        ctx.canvas.width = canvasWidth;
        ctx.canvas.height = canvasHeight;

        maxRadius = Math.round(0.27 * vmax);
        maxWidth = Math.round(0.66 * vmax);
        height = maxWidth * 0.25;
    });

    function draw() {
        if (paused) {
            requestAnimationFrame(draw);
            return;
        }

        const scrollY = window.pageYOffset / 6;

        let DX;
        let DY;

        if (_.isMobile) {
            DX = o[0] * (canvasWidth / 2);
            DY = o[1] * (canvasHeight / 2);
        } else {
            DX = mx - (canvasWidth / 2);
            DY = my - (canvasHeight / 2);
        }

        circleOffset[0] = lerp(circleOffset[0], -DX * 0.4, smooth);
        circleOffset[1] = lerp(circleOffset[1], -DY * 0.4, smooth) - scrollY;

        rectOffset[0] = lerp(rectOffset[0], -DX * 0.5, smooth);
        rectOffset[1] = lerp(rectOffset[1], -DY * 0.5, smooth) - scrollY;

        curveOffset[0] = lerp(curveOffset[0], -DX * 0.3, smooth);
        curveOffset[1] = lerp(curveOffset[1], -DY * 0.3, smooth) - scrollY;

        if (_.isMobile) {
            imageOffset[0] = lerp(imageOffset[0], -DX * 0.2, smooth);
            imageOffset[1] = lerp(imageOffset[1], -DY * 0.2, smooth);

            image.style.transform = `translate3d(${imageOffset[0]}px, ${imageOffset[1]}px, 0) scale(1.2)`;
        } else {
            imageOffset[0] = lerp(imageOffset[0], -DX * 0.05, smooth);
            imageOffset[1] = lerp(imageOffset[1], -DY * 0.05, smooth);

            image.style.transform = `translate3d(${imageOffset[0]}px, ${imageOffset[1]}px, 0) scale(1.05)`;
        }

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const now = performance.now();

        if (!startTime || progress > 1) {
            startTime = now;
            currentPos = (currentPos === 2) ? 0 : currentPos + 1;
        }
        const dt = now - startTime;
        progress = dt / 3000;

        // cricle
        // scale 0 -> 1: 1.8s
        // fade out: 1.2s
        {
            let opacity;
            ctx.save();

            if (progress >= 0.6) {
                radius = maxRadius;
                opacity = 2 * (1 - progress) // 0.6: 0.8,, 1: 0
            } else {
                radius = Math.round((1 / 0.6) * progress * maxRadius);
                opacity = 0.8;
            }

            const dx = (pos.circle[currentPos].x * canvasWidth);
            const dy = (pos.circle[currentPos].y * canvasHeight);

            ctx.translate(dx + circleOffset[0], dy + circleOffset[1]);
            ctx.beginPath();
            ctx.arc(canvasWidth / 2, canvasHeight / 2, radius, 0, Math.PI*2);
            ctx.fillStyle = `rgba(209, 190, 241, ${opacity})`;
            ctx.fill();
            ctx.restore();
        }

        // rect
        rect: {
            let lineStart;
            let lineEnd;
            let opacity = 0.8;
            ctx.save();

            if (progress < 0.4 / 3) {
                break rect;
            }
            if (progress < 1.9 / 3) {
                width = Math.round((1 / 0.5) * (progress - 0.4 / 3) * maxWidth);
                lineStart = (canvasWidth / 2) - (maxWidth / 2);
                lineEnd = lineStart + width;
            } else if(progress >= 1.9 / 3 && progress < 2.1/3) {
                lineStart = (canvasWidth / 2) - (maxWidth / 2);
                lineEnd = lineStart + maxWidth;
            } else if (progress >= 2.1 / 3 && progress <= 1) {
                const a = (progress - (2.1/3)) / (1 - (2.1/3));
                lineStart = (canvasWidth / 2) + (maxWidth / 2);
                lineEnd = lineStart - (maxWidth * (1-a));

                opacity = 0.8 - (0.6 * a);
            } else {
                break rect;
            }

            /*width = width + 5;
            if (width >= maxWidth) {
                width = 0;
            }*/
            //ctx.rotate((Math.PI/180)*25);

            const dx = (pos.rect[currentPos].x * canvasWidth);
            const dy = (pos.rect[currentPos].y * canvasHeight);

            ctx.translate(canvasWidth / 2 + rectOffset[0], canvasHeight / 2 + rectOffset[1]);
            ctx.rotate((Math.PI / 180) * pos.rect[currentPos].rot);
            ctx.translate((canvasWidth / -2) + dx, (canvasHeight / -2) + dy);
            ctx.beginPath();
            ctx.moveTo(lineStart, canvasHeight / 2);
            ctx.lineTo(lineEnd, canvasHeight / 2);
            ctx.lineWidth = height;
            ctx.strokeStyle = `rgba(139, 92, 221, ${opacity})`;
            ctx.stroke();
            ctx.restore();
        }

        arc: {
            let p = dt / 2900; // progress >=0, <=1
            if (p > 1) {
                break arc;
            }
            let startAngle;
            let endAngle;
            let t;
            if (p < 0.5) {
                t = p * 2;
                t = eval_curve(t);
                startAngle = 0;
                endAngle = lerp(0, 90, t);
            } else {
                t = (p - 0.5) * 2;
                t = eval_curve(t);
                startAngle = lerp(0, 90, t);
                endAngle = 90;
            }

            const dx = (pos.arc[currentPos].x * canvasWidth);
            const dy = (pos.arc[currentPos].y * canvasHeight);

            ctx.save();
            ctx.beginPath();
            ctx.translate(canvasWidth/2 + curveOffset[0], canvasHeight/2 + curveOffset[1]);
            ctx.rotate((Math.PI/180) * 30);
            ctx.translate(dx, dy);
            ctx.arc(0, 0, (vmax * 0.18), (Math.PI/180) * startAngle, (Math.PI/180) * endAngle);
            ctx.lineWidth = (vmax * 0.09);
            ctx.strokeStyle = 'rgba(62, 55, 80, 0.8)';
            ctx.stroke();
            ctx.restore();
        }

        window.requestAnimationFrame(draw);
    }
    window.requestAnimationFrame(draw);


    function eval_curve(t) {
        const b = [0.0, 0.0, 0.25, 0.0, 0.75, 1.0, 1.0, 1.0];

        const u = 1.0 - t;
        const tt = t * t;
        const uu = u * u;
        const uuu = uu * u;
        const ttt = tt * t;
        return (uuu * b[1]) + (3 * uu * t * b[3]) + (3 * u * tt * b[5]) + (ttt * b[7]);
    }
    function lerp(a, b, t) {
        return (1-t) * a + t * b;
    }

    if (_.isMobile) {
        if (window.DeviceOrientationEvent) {
            window.addEventListener('deviceorientation', (e) => {
                if (paused) return;
                o[0] = e.gamma / 90;
                o[1] = e.beta / 180;
            });
        }
    } else {
        window.addEventListener('mousemove', (e) => {
            if (paused) return;
            mx = e.pageX;
            my = e.pageY;
        });
    }
}

export function init_events() {
    let TW = Gsap.TweenMax;
    let TL = Gsap.TimelineMax;

    function find_elements(selectors){
        let root = document;
        for(let k in selectors)
        {
            let selection = root.querySelectorAll(selectors[k]);
            if(selection.length > 1) selectors[k] = selection;
            else selectors[k] = selection[0];
        }
        return selectors;
    }

    function get_svg_element_center(el) {
        let bounds = el.getBoundingClientRect();
        let cx = bounds.left + ((bounds.right - bounds.left) / 2);
        let cy = bounds.bottom + ((bounds.top - bounds.bottom) / 2);
        return [cx, cy];
    }

    function hero_animation() {
        let E = find_elements(
        {
            hero: '.hero',
            hero_group: '.hero-shape-group',
            shapes: '.hero-shape',
            squiggles: '.hero-squiggle',
            squig_parents: '.hero-squiggle-parent',
            arcs: '.hero-arc',
            discs: '.hero-disc',
            rings: '.hero-ring',
            rects: '.hero-rect',
        });

        // Get hero dimensions

        let width = E.hero.clientWidth;
        let height = E.hero.clientHeight;
        let hw = width / 2;
        let hh = height / 2;

        if(height > width)
        {
            TW.set(E.hero_group, {svgOrigin:"960 540", rotation:90}, 0);
        }

        // Timelines
        let tl = new TL({paused:true});
        let tl_shapes = new TL();
        let tl_arcs = new TL({repeat:-1});
        let tl_rings = new TL({repeat:-1});
        let tl_discs = new TL({repeat:-1});
        let tl_squiggles = new TL({repeat:-1});
        let tl_rects = new TL({repeat:-1});

        // Shapes

        for(let i = 0; i < E.shapes.length; ++i)
        {
            let t = (i * 0.02);
            let s = E.shapes[i];
            let pos = get_svg_element_center(s);

            let d = 0.4;
            let dx = (hw - pos[0]) * d;
            let dy = (hh - pos[1]) * d;

            tl_shapes.from(s, 1, { opacity:0 }, t);
            tl_shapes.from(s, 1.2, { x:dx, y:dy, ease:Back.easeInOut.config(4)}, t);
        }

        // Arcs

        for(let i = 0; i < E.arcs.length; ++i)
        {
            let t = i * Math.random();
            let arc = E.arcs[i];
            let len = arc.getTotalLength();
            arc.style.strokeDasharray = len + ' ' + len;
            arc.style.strokeDashoffset = len;
            tl_arcs.to(arc, 1, { strokeDashoffset:0, ease:Power2.easeOut }, t);
            tl_arcs.to(arc, 1, { strokeDashoffset:-len, ease:Power2.easeOut }, t + 1);
        }

        // Rings

        for(let i = 0; i < E.rings.length; ++i)
        {
            let t = i * Math.random();
            let ring = E.rings[i];
            // Cache values, because firefox
            // let len = ring.getTotalLength();
            let len;
            if (i === 0) {
                len = 322.99853515625;
            } else if (i === 1) {
                len = 102.62995147705078;
            } else {
                len = 102.6300048828125;
            }
            ring.style.strokeDasharray = len + ' ' + len;
            ring.style.strokeDashoffset = len;
            tl_arcs.to(ring, 1, { strokeDashoffset:0, ease:Power2.easeOut }, t);
            tl_arcs.to(ring, 1, { strokeDashoffset:-len, ease:Power2.easeOut }, t + 1);
        }

        // Squiggles

        for(let i = 0; i < E.squig_parents.length; ++i)
        {
            TW.set(E.squig_parents[i], {rotation: 300});
        }
        for(let i = 0; i < E.squiggles.length; ++i)
        {
            tl_squiggles.to(E.squiggles[i], 1.5, {x:82, ease:Power0.easeNone}, 0);
        }

        // Discs

        for(let i = 0; i < E.discs.length; ++i)
        {
            let d = E.discs[i];
            let t = i * Math.random();
            tl_discs.fromTo(d, 0.5, { scale:0 }, { scale:1, ease:Back.easeOut }, t);
            tl_discs.to(d, 0.5, { scale:1 }, t + 0.5);
            tl_discs.to(d, 0.5, { scale:0 }, t + 1);
        }

        // Rectangles

        for(let i = 0; i < E.rects.length; ++i)
        {
            let t = i * Math.random();
            let rect = E.rects[i];
            // Cache values, because firefox
            //let len = rect.getTotalLength();
            let len;
            if (i === 0) {
                len = 57.84137725830078;
            } else {
                len = 57.83936309814453;
            }
            rect.style.strokeDasharray = len + ' ' + len;
            rect.style.strokeDashoffset = len;
            tl_rects.to(rect, 1.0, { strokeDashoffset:0, ease:Power2.easeOut }, t);
            tl_rects.to(rect, 0.3, { strokeDashoffset:-len, ease:Power2.easeOut }, t + 1);
        }

        // Sequencing

        let delay = 0.3;
        tl.add(tl_shapes, 0.3);
        tl.add(tl_rings, delay);
        tl.add(tl_arcs, delay);
        tl.add(tl_squiggles, delay);
        tl.add(tl_discs, delay);
        tl.add(tl_rects, delay);
        tl.timeScale(0.8);
        return tl;
    }

    // Will revisit canvisifying this anim. Blocked on mobile for now.
    if (!_.isMobile) {
        hero_animation().play();
    }
}



// WEBPACK FOOTER //
// ./assets/js/components/_hero.js